import { getLogger } from '@blueberry/observability/logging.js'
import { getOpenReplayTracker } from '@blueberry/observability/open_replay.js'
import { TrackDxEvent } from '../../dx/lib/analytics/dx.js'
import { wait } from '../utils'
import { dev } from './config.js'

export type UseWebSocketOpts<T> = {
  url: string

  /**
   * Stop trying to reconnect after this many ms.
   * If we can't connect after this many ms, we'll show `unable_to_connect_message` alert.
   * @default 30000 (30 seconds)
   */
  maxRetryTimeMS?: number

  /**
   * How long to wait before retrying to connect.
   * Doubles after each failure.
   * @default 500
   */
  retryTimeMS?: number

  /**
   * Optional. If set, the websocket will be disconnected after this many ms of inactivity.
   * Once disconnected, `onIdleDisconnect` will be called.
   */
  idleDisconnectTimeoutMS?: number

  unableToConnectMessage?: string

  onMessage: (message: T) => void

  onReadyStateChange?: (state: WebSocketReadyState) => void

  onIdleDisconnect?: () => void

  onError?: (event: Event) => void

  onParseFailure?: (message: string, error: unknown) => void
}

export type WebSocketReadyState = 'connecting' | 'open' | 'closing' | 'closed'

export type WebSocketState<T> = {
  send: (data: T) => void
  teardown: () => void
}

export const useWebSocket = <INCOMING, OUTGOING = null>(
  opts: UseWebSocketOpts<INCOMING>,
): WebSocketState<OUTGOING> => {
  const id = getUniqueId()
  const logger = getLogger('web_sockets', { metadata: { id, url: opts.url } })
  const protocol = window.location.protocol === 'http:' ? 'ws' : 'wss'
  const host = `${protocol}://${window.location.host}` // must be same as origin for cookies

  const openReplayWsHook = getOpenReplayTracker()?.trackWs(opts.url)

  const maxRetryTimeMS = opts.maxRetryTimeMS ?? 30_000
  let retryTimeMS = opts.retryTimeMS ?? 500
  let ws: WebSocket | null = null
  // ws consumer manually closed the connection, so we should not retry
  let manuallyClosed = false

  const attemptToReconnectWithBackoff = async () => {
    if (manuallyClosed) {
      return
    }

    // Stop trying after `maxRetryTimeMS` (30 seconds)
    if (retryTimeMS >= maxRetryTimeMS) {
      const alert =
        opts.unableToConnectMessage ||
        'Unable to synchronize. Please refresh the page to try again.'
      if (dev) {
        // window.alert in dev is noisy, so we use logger.error instead
        logger.error(alert)
      } else {
        window.alert(alert)
      }
      return
    }

    await wait(retryTimeMS)

    if (!ws) {
      logger.error('Cannot retry websocket connection, ws is not initialized')
      return
    }

    if (ws.readyState == ws.OPEN) {
      logger.info('Skipping retry. Socket is already open')
      return
    }

    setWebsocket('Attempting to reconnect')

    retryTimeMS *= 2
  }

  const getState = (): WebSocketReadyState => {
    if (!ws) return 'closed'
    if (ws.readyState == ws.OPEN) return 'open'
    if (ws.readyState == ws.CLOSING) return 'closing'
    if (ws.readyState == ws.CLOSED) return 'closed'
    if (ws.readyState == ws.CONNECTING) return 'connecting'
    logger.error('Unknown ready state', ws.readyState)
    return 'connecting'
  }

  const resetBackoff = () => {
    retryTimeMS = opts.retryTimeMS ?? 500
  }

  const updateReadyState = () => {
    if (manuallyClosed) {
      logger.info(
        'Skipping ready state notification. Socket was manually closed',
        getState(),
      )
      return
    }

    logger.info('Ready state changed', getState())
    opts.onReadyStateChange?.(getState())
  }

  const onError = (event: Event) => {
    logger.error('received an error', event)
    if (opts.onError) {
      opts.onError(event)
    }
  }

  const onParseFailure =
    opts.onParseFailure ??
    ((event: MessageEvent, e) => {
      logger.error('Failed to parse message', ws?.url, event.data, e)
    })

  const ws_state: WebSocketState<OUTGOING> = {
    teardown: () => {
      if (!ws) {
        logger.error('Cannot close websocket, ws is not initialized')
        return
      }
      manuallyClosed = true
      ws.close()
    },
    send: (data: OUTGOING) => {
      if (!ws) {
        logger.error('Cannot send message, ws is not initialized', data)
        return
      }
      if (getState() !== 'open') {
        // TODO: queue messages?
        logger.error('Cannot send message, socket not open', data)
        return
      }
      openReplayWsHook?.('message', JSON.stringify(data), 'up')
      ws.send(JSON.stringify(data))
    },
  }

  const setWebsocket = (reason: string): WebSocket => {
    logger.info('Setting websocket', {
      currentState: ws ? getState() : 'initializing',
      reason,
    })

    ws?.close()

    updateReadyState()
    ws = new WebSocket(`${host}${opts.url}`)
    updateReadyState()

    ws.onerror = (ev) => {
      updateReadyState()
      openReplayWsHook?.('error', `${ev}`, 'down')
      onError(ev)
    }

    ws.onclose = (event) => {
      if (!manuallyClosed) {
        // Report error to OpenReplay & call our track event endpoint to aid in debugging websocket issues for this ticket: https://github.com/sparkmd/blueberry-monorepo/issues/3927
        const metadata = {
          ws_url: ws?.url ?? '',
          code: event.code, // https://developer.mozilla.org/en-US/docs/Web/API/CloseEvent/code
          reason: event.reason,
          wasClean: event.wasClean,
        }
        void TrackDxEvent.webSocketConnectionClosed(metadata)
        const tracker = getOpenReplayTracker()
        if (tracker) {
          tracker.handleError(Error('Websocket connection closed'), metadata)
        }
        attemptToReconnectWithBackoff()
      }

      updateReadyState()
    }

    ws.onopen = () => {
      resetBackoff()
      updateReadyState()
    }

    ws.onmessage = (event) => {
      openReplayWsHook?.('message', event.data, 'down')

      resetBackoff()

      let parseResult: INCOMING
      try {
        parseResult = JSON.parse(event.data)
      } catch (e) {
        // We don't forward messages that cannot be parsed.
        onParseFailure(event.data, e)
        return
      }

      try {
        opts.onMessage(parseResult)
      } catch (e) {
        logger.error('Failed to process message', parseResult, e)
      }
    }

    return ws
  }

  if (opts.idleDisconnectTimeoutMS) {
    const maxIdleTimeMs = opts.idleDisconnectTimeoutMS
    let idleTime = 0

    window.addEventListener('mousemove', () => {
      idleTime = 0
    })

    const interval = setInterval(() => {
      idleTime += 1000

      if (idleTime < maxIdleTimeMs) {
        return
      }

      logger.info('Disconnecting websocket due to inactivity')

      ws_state.teardown()
      clearInterval(interval)

      if (opts.onIdleDisconnect) {
        opts.onIdleDisconnect()
      } else {
        logger.info('Disconnected websocket due to inactivity')
      }
    }, 1000)
  }

  setWebsocket('initialize')

  return ws_state
}

const getUniqueId = () => {
  return Math.random().toString(36).substring(2)
}
